/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import "firebase/compat/firestore";
import "firebase/compat/auth";
import "youbora-adapter-html5";
import { Box, Slider } from "@material-ui/core";
import { useI18n } from "hooks/i18n/i18n";
import { useEmoBalance } from "hooks/paw/useEmoBalance";
import React, { useState } from "react";

import { useSelector } from "../../store";
import { StreamingAnnouncementForFullscreen } from "./StreamingAnnouncementForFullscreen";
import StreamingCommentV2 from "./StreamingCommentV2";
import { StreamingSelectGiftV2 } from "./StreamingSelectGiftV2";
import { StreamingSendCommentV2 } from "./StreamingSendCommentV2";
import { useSendComment } from "./useSendComment";

import type { StreamingType } from "../../modules/streaming";
import type { Store } from "../../store";
import type { EventInfo } from "../../utility";
import type { PlainVideoPlayer } from "./videojsPlayer";
import type { Timestamp } from "@google-cloud/firestore";
import type { ChangeEvent, FC } from "react";

export const StreamingFullscreen: FC<{
  children: React.ReactNode;
  fullscreenRef: React.RefObject<HTMLDivElement>;
  player: PlainVideoPlayer | null;
  eventInfo: EventInfo;
  streamingType: StreamingType;
  eventStart: Timestamp;
  mainVideoId: string | undefined;
  isDisplayActionArea: boolean;
  isOpenAnnouncement: boolean;
  streamingAnnouncement: string;
  handleCloseAnnouncement: () => void;
  isFullscreen: boolean;
  hasDvrStartTime: boolean;
  isPollingEnabled: boolean;
}> = ({
  fullscreenRef,
  children,
  player,
  eventInfo,
  streamingType,
  eventStart,
  mainVideoId,
  isDisplayActionArea,
  isOpenAnnouncement,
  streamingAnnouncement,
  handleCloseAnnouncement,
  isFullscreen,
  hasDvrStartTime,
  isPollingEnabled,
}) => {
  const CSS_PLAYER_HEIGHT = 68 + 8; // プレイヤーコントロール部分の高さ + 余白
  const CSS_SCREEN_PADDING = 24;

  const { t } = useI18n();
  const { paidValue: emoBalance } = useEmoBalance();

  const user = useSelector((state: Store) => state.auth.user);
  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  const [opacity, setOpacity] = useState<number>(100);

  const {
    commentInputRef,
    iconUrls,
    comment,
    giftItems,
    selectedGiftItem,
    canSendComment,
    canPurchaseGiftItem,
    isOpenGiftDrawer,
    handleSetComment,
    handleSendComment,
    handleOpenGiftDrawer,
    handleCloseGiftDrawer,
    handleClickSelectedGift,
    handleOpenPawChargeModal,
  } = useSendComment({
    mainVideoId,
  });

  const handleOpacitySlider = (
    _: ChangeEvent<Record<string, never>>,
    newValue: number | number[]
  ) => {
    const newOpacity = Array.isArray(newValue) ? newValue[0] : newValue;
    if (typeof newOpacity === "number") {
      setOpacity(newOpacity);
    }
  };

  return (
    // material-uiのBoxにrefは渡せないため、divでラップする
    <div
      id={"streaming-fullscreen"}
      ref={fullscreenRef}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Box
        style={{
          position: "relative",
          width: "100%",
          ...(isFullscreen && {
            height: "calc(100vw * 9 / 16)",
            maxHeight: "100vh",
            maxWidth: "calc(100vh * 16 / 9)",
            margin: "auto",
          }),
        }}
      >
        {children}

        {/* コメント欄を表示する */}
        {/* フルスクリーンでない時、isFullscreenで表示の制御を行わないと、この要素は表示されてしまうため条件分岐に含めている。 */}
        {isFullscreen && isDisplayActionArea && mainVideoId && (
          <Box
            id={"fullscreen-streaming-comment"}
            position={"absolute"}
            top={"12px"}
            right={"12px"}
            display={"flex"}
            flexDirection={"column"}
            gridGap={"12px"}
            width={"360px"}
            height={`calc(100vw / (16 / 9) - ${CSS_PLAYER_HEIGHT}px - ${CSS_SCREEN_PADDING}px)`}
            maxHeight={`calc(100vh - ${CSS_PLAYER_HEIGHT}px - ${CSS_SCREEN_PADDING}px)`}
            padding={"12px"}
            bgcolor={isDarkModeEnabled ? "#000" : "#fff"}
            borderRadius={"8px"}
            style={{
              display: "none",
              opacity: opacity / 100,
            }}
          >
            <StreamingAnnouncementForFullscreen
              streamingType={streamingType}
              isAdmin={isAdmin}
              isOpenAnnouncement={isOpenAnnouncement}
              streamingAnnouncement={streamingAnnouncement}
              handleCloseAnnouncement={handleCloseAnnouncement}
            />

            <Box flex={"1 1 auto"}>
              <StreamingCommentV2
                player={player}
                eventStart={eventStart}
                streamingType={streamingType}
                eventInfo={eventInfo}
                mainVideoId={mainVideoId}
                selectedCommentFilter={null}
                hasDvrStartTime={hasDvrStartTime}
                isFullScreen={true}
                isPollingEnabled={isPollingEnabled}
              />
            </Box>

            {isOpenGiftDrawer && giftItems.length > 0 && (
              <StreamingSelectGiftV2
                isDarkModeEnabled={isDarkModeEnabled}
                canPurchaseGiftItem={canPurchaseGiftItem}
                iconUrls={iconUrls?.["32"]}
                userName={user.displayName ?? ""}
                comment={comment}
                emoBalance={emoBalance}
                giftItems={giftItems}
                selectedGiftItem={selectedGiftItem}
                canSendComment={canSendComment}
                onClickSendComment={handleSendComment}
                onClickCloseGiftDrawer={handleCloseGiftDrawer}
                onClickSelectedGift={handleClickSelectedGift}
                onClickOpenPawChargeModal={handleOpenPawChargeModal}
              />
            )}

            {streamingType === "Live" && user.uid && (
              <StreamingSendCommentV2
                isDarkModeEnabled={isDarkModeEnabled}
                isOpenGiftDrawer={isOpenGiftDrawer}
                isSelectedGift={selectedGiftItem !== null}
                canPurchaseGiftItem={canPurchaseGiftItem}
                userIcon={iconUrls?.["32"]}
                userName={user.displayName ?? ""}
                inputRef={commentInputRef}
                comment={comment}
                canSendComment={canSendComment}
                giftItems={giftItems}
                onClickSendComment={handleSendComment}
                setComment={handleSetComment}
                onClickOpenGiftDrawer={handleOpenGiftDrawer}
                onClickOpenPawChargeModal={handleOpenPawChargeModal}
              />
            )}

            <Box marginTop={"-8px"}>
              <Slider
                value={opacity}
                min={20}
                max={100}
                aria-label={t("streaming.fullscreen.commentOpacity")}
                onChange={handleOpacitySlider}
                css={css`
                  padding: 0px !important;
                  .MuiSlider-rail {
                    background-color: #ebebeb;
                  }
                  .MuiSlider-track {
                    background-color: #3182ce;
                  }
                  .MuiSlider-thumb {
                    background-color: #fff;
                    box-shadow:
                      0px 1px 2px 0px rgba(0, 0, 0, 0.1),
                      0px 2px 4px 0px rgba(0, 0, 0, 0.1);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                  }
                `}
              />
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
};
